import React, { useEffect, useState, useCallback } from 'react'
import { get, has, isEmpty, forEach, keys, throttle } from 'lodash'
import axios from 'axios'
import {
  MenuContext,
  BookInfo,
  SelectionContext,
  AnnotationContext,
  UserInfo,
  Errors
} from './menuContext'

const drupalSettings = { nclc_book: { bookReader: { data: [{ url: '/book/test-book', type: 'book' }] } } }

const GlobalContext = ({ children }) => {
  const [selection, setFuncSelection] = useState({})
  const [annotationObj, setAnnotationObj] = useState({})
  const [menuStatus, changeMenuStatus] = useState(true)
  const [userClosedMenu, setUserClosedMenu] = useState(false)
  const [rightSidebar, changeRightSidebar] = useState(false)
  const [error, setError] = useState('')
  const [timer, setTimer] = useState(0)
  const [redirectPath, setRedirectPath] = useState(window.location.pathname)
  const { nclc_book: { bookReader: bookSettings } } = window.drupalSettings || drupalSettings
  const bookInfo = bookSettings.data.find((data) => {
    return data.type === 'book'
  })
  const defaultPage = bookSettings.data.find((data) => {
    return data.type === 'subsection'
  })

  const defaultUser = bookSettings.data.find((data) => {
    return data.type === 'user'
  })

  const defaultSectionType = get(defaultPage, 'section_type')
  if (defaultSectionType === 'appendix' || defaultSectionType === 'case commentary') {
    bookInfo.page_type = 'appendices'
  } else {
    bookInfo.page_type = 'chapters'
  }

  const [user, setUser] = useState(defaultUser)
  const [page, updatePage] = useState(has(defaultPage, 'id') ? defaultPage.id : null)
  const [book, setBook] = useState(bookInfo)
  const [forceScroll, setForceScroll] = useState(false)
  // State will be using for Context.
  const [overlay, setOverlay] = useState(false)
  const [csrfToken, setCsrfToken] = useState('')

  useEffect(() => {
    axios.get(window.Drupal.url('session/token')).then((data) => { setCsrfToken(data.data) })
  }, [])

  useEffect(() => {
    setForceScroll(true)
  }, [])
  useEffect(() => {
    const defaultContent = get(book, 'content')
    if (defaultContent) {
      forEach(defaultContent, (item, id) => {
        sessionStorage.setItem(id, JSON.stringify(item))
      })
    }
  }, [book])
  const saveSelection = (containerEl) => {
    const range = window.getSelection().getRangeAt(0)
    const preSelectionRange = range.cloneRange()
    preSelectionRange.selectNodeContents(containerEl)
    preSelectionRange.setEnd(range.startContainer, range.startOffset)
    const start = preSelectionRange.toString().length
    return {
      start,
      end: start + range.toString().length
    }
  }
  const setPage = useCallback(throttle((id) => {
    if (id === page) {
      return;
    }

    if (Date.now() - timer > 500) {
      updatePage(id)
      setTimer(Date.now())
      if (get(book, 'page_type') === 'chapters' && !keys(get(book, 'content')).includes(id)) {
        axios.post('/bookreader/recently_viewed', {
          subsection: id
        }, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        }).then((response) => {
          const err = get(response, 'data.data[0].errors')
          if (err) {
            setError(err[0])
          }
        })
      }
    }
  }, 1000, {trailing: true}), [csrfToken, timer])

  function componentFromStr(numStr, percent) {
    const num = Math.max(0, parseInt(numStr, 10))
    return percent ? Math.floor(255 * Math.min(100, num) / 100) : Math.min(255, num)
  }

  const rgbRegex = /^rgb\(\s*(-?\d+)(%?)\s*,\s*(-?\d+)(%?)\s*,\s*(-?\d+)(%?)\s*\)$/
  const hexRegex = /^#?([a-f\d]{6})$/
  const shortHexRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/

  function Colour(r, g, b) {
    // Make a new Colour object even when Colour is not called with the new operator
    if (!(this instanceof Colour)) {
      return new Colour(r, g, b)
    }

    if (typeof g === 'undefined') {
      // Parse the colour string
      const colStr = r.toLowerCase(); let
        result

      // Check for hex value first, the short hex value, then rgb value
      if ((result = hexRegex.exec(colStr))) {
        const hexNum = parseInt(result[1], 16)
        r = hexNum >> 16
        g = (hexNum & 0xff00) >> 8
        b = hexNum & 0xff
      } else if ((result = shortHexRegex.exec(colStr))) {
        r = parseInt(result[1] + result[1], 16)
        g = parseInt(result[2] + result[2], 16)
        b = parseInt(result[3] + result[3], 16)
      } else if ((result = rgbRegex.exec(colStr))) {
        r = componentFromStr(result[1], result[2])
        g = componentFromStr(result[3], result[4])
        b = componentFromStr(result[5], result[6])
      } else {
        throw new Error(`Colour: Unable to parse colour string '${colStr}'`)
      }
    }

    this.r = r
    this.g = g
    this.b = b
  }

  Colour.prototype = {
    equals(colour) {
      return this.r === colour.r && this.g === colour.g && this.b === colour.b
    }
  }

  const unhighlight = (node, colour = '#ff0') => {
    if (!(colour instanceof Colour)) {
      colour = new Colour(colour)
    }

    if (node.nodeType === 1) {
      const bg = node.style.backgroundColor
      if (bg && colour.equals(new Colour(bg))) {
        node.style.backgroundColor = ''
      }
    }
    let child = node.firstChild
    while (child) {
      unhighlight(child, colour)
      child = child.nextSibling
    }
  }

  const setSelection = (n) => {
    setFuncSelection(n)
  }
  const restoreSelection = (containerEl, savedSel) => {
    if (!isEmpty(savedSel) && !isEmpty(containerEl)) {
      let charIndex = 0
      const range = document.createRange()
      range.setStart(containerEl, 0)
      range.collapse(true)
      const nodeStack = [containerEl]
      let foundStart = false
      let stop = false
      let node
      while (!stop && (node = nodeStack.pop())) {
        if (node.nodeType === 3) {
          const nextCharIndex = charIndex + node.length
          if (!foundStart && savedSel.start >= charIndex && savedSel.start <= nextCharIndex) {
            range.setStart(node, savedSel.start - charIndex)
            foundStart = true
          }
          if (foundStart && savedSel.end >= charIndex && savedSel.end <= nextCharIndex) {
            range.setEnd(node, savedSel.end - charIndex)
            stop = true
          }
          charIndex = nextCharIndex
        } else {
          let i = node.childNodes.length
          while (i--) {
            nodeStack.push(node.childNodes[i])
          }
        }
      }

      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    }
  }
  return (
    <SelectionContext.Provider value={[selection, setSelection, restoreSelection, redirectPath, setRedirectPath]}>
      <AnnotationContext.Provider value={[annotationObj, setAnnotationObj, saveSelection]}>
        <MenuContext.Provider value={[menuStatus, changeMenuStatus, rightSidebar, changeRightSidebar, userClosedMenu, setUserClosedMenu]}>
          <BookInfo.Provider value={[book, setBook, page, setPage, forceScroll, setForceScroll, overlay, setOverlay, csrfToken]}>
            <UserInfo.Provider value={[user, setUser]}>
              <Errors.Provider value={[error, setError]}>
                {children}
              </Errors.Provider>
            </UserInfo.Provider>
          </BookInfo.Provider>
        </MenuContext.Provider>
      </AnnotationContext.Provider>
    </SelectionContext.Provider>
  )
}

export default GlobalContext
